import {
  Alert,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
} from "@mui/material";
import {
  DarkMode,
  HelpRounded,
  LightMode,
  LogoutOutlined,
  SwapHorizOutlined,
} from "@mui/icons-material";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import PersonIcon from "@mui/icons-material/Person";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import React, { useCallback, useMemo, useState } from "react";

import CxEmployeeAvatar, { AvatarSize } from "@components/CxEmployeeAvatar";

import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import { NavLink as RouterNavLink } from "react-router-dom";

import { UnityExperienceStatus } from "@lib/ShiOneClient";
import ForkLeftIcon from "@mui/icons-material/ForkLeft";
import { useThemeMode } from "../../../../theme/ThemeModeProviderContext";
import { useClaims } from "../../../../auth/ClaimsProvider";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import useGetCurrentUnityStatus from "@features/unified-user-header-feature/hooks/useGetCurrentUnityStatus";
import { useUnityStatusCookie } from "@features/unified-user-header-feature/hooks/useUnityStatusCookie";
import useUpdateUnityStatus from "@features/unified-user-header-feature/hooks/useUpdateUnityStatus";
import { ShiOnePermission } from "../../../../auth/authorizationTypes";
import {
  isImpersonatingAccount,
  isImpersonatingUser,
} from "shared-ui/src/utils/clientmanagement";
import {
  AVATAR_CONFIG,
  MENU_ITEM_CONTEXT_SWITCH_TYPOGRAPHY,
} from "@features/unified-user-header-feature/types/constants";
import { AppendIfPrefixExists } from "../../../../modules/sitePaths";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { useStorefrontSwitcher } from "@features/unified-side-nav-feature/storefront-switcher/StorefrontSwitcherContext";
import UseLegacyShiComDialog from "@features/unified-user-header-feature/components/UnityOptOutDialog";
import useProfileDrawersEnabled from "@features/unified-user-header-feature/hooks/useProfileDrawersEnabled";

interface UserMenuProps {
  user?: {
    displayName: string;
    firstName: string;
    lastName: string;
    email: string;
    isShiUser: boolean;
  };
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onOpenUserProfileDrawer?: () => void;
}

const getInitial = (name: string): string | null => {
  if (!name) return null;
  const value = name.trim();
  if (value.length === 0) return null;
  return value.trim().charAt(0).toUpperCase();
};

const DarkModeBetaWarning =
  "Please note that Dark Mode is still under construction. Certain sections of SHI One may not be usable while in Dark Mode. We apologize for any inconvenience.";

const CourtesyUserMenu: React.FC<UserMenuProps> = ({
  user,
  anchorEl,
  open,
  onClose,
  onOpenUserProfileDrawer,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const { mode: themeMode, toggleThemeMode } = useThemeMode();
  const { hasPermission, getClaimValue } = useClaims();

  const { enabled: addressManagementEnabled } = useFeatureFlag(
    "cartographers.enable-address-management",
  );

  const profileDrawersEnabled = useProfileDrawersEnabled();
  const { data: unityStatusData } = useGetCurrentUnityStatus();
  const { updateStatus } = useUnityStatusCookie();

  const updateUnityStatusMutation = useUpdateUnityStatus();

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleConfirmRevert = () => {
    updateUnityStatusMutation.mutate(UnityExperienceStatus.OptedOut, {
      onSuccess: () => {
        updateStatus(UnityExperienceStatus.OptedOut);
        window.location.href = shiComBaseUrl + "/";
      },
      onError: () => {
        setSnackbarMessage(
          "Failed to switch to legacy experience. Please try again.",
        );
        setSnackbarOpen(true);
        setDialogOpen(false);
      },
    });
  };

  const storefrontSwitcher = useStorefrontSwitcher();
  const changeOrgUnit = storefrontSwitcher.enabled
    ? storefrontSwitcher.canOpenStorefront || storefrontSwitcher.canOpenTenant
    : hasPermission(ShiOnePermission.orgUnitChange);
  const switchLabel = storefrontSwitcher.enabled ? "SWITCH STOREFRONT" : "SWITCH CATALOG";
  const account =
    hasPermission(ShiOnePermission.userSettingsView) ||
    hasPermission(ShiOnePermission.userSettingsUpdate);
  const messages = hasPermission(ShiOnePermission.newsCenterRead);

  const isImpersonating =
    getClaimValue("isImpersonating")?.toLowerCase() === "true";

  const userEntraId =
    user?.isShiUser &&
    !isImpersonatingAccount() &&
    !isImpersonatingUser() &&
    !isImpersonating
      ? getClaimValue("userEntraId")
      : null;

  const shiComBaseUrl = useMemo(() => getShiComBaseUrl(), []);

  const userAvatar = useMemo(() => {
    if (!user) {
      return null;
    }
    if (userEntraId && user.displayName) {
      return (
        <CxEmployeeAvatar
          entraId={userEntraId}
          displayName={user.displayName}
          photoSize={AvatarSize.medium}
        />
      );
    }
    const first = getInitial(user.firstName);
    const last = getInitial(user.lastName);
    const initials = [first, last].filter(Boolean).join("") || getInitial(user.email);
    if (initials == null) {
      return null;
    }
    return (
      <Avatar component="span" sx={AVATAR_CONFIG}>
        {initials}
      </Avatar>
    );
  }, [user, userEntraId]);

  const handleToggleThemeMode = useCallback(() => {
    onClose();
    toggleThemeMode();
  }, [onClose, toggleThemeMode]);

  const handleLinkKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (event.key === " ") {
        event.preventDefault();
        onClose();
        event.currentTarget.click();
      }
    },
    [onClose],
  );

  return (
    <>
      <Menu
        id="unity-courtesy-header-user-menu"
        aria-label={`Courtesy User Menu: ${user?.displayName}`}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        elevation={8}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              mt: -1,
              backgroundColor: "background.default",
              minWidth: { xs: "280px", sm: 300 },
              maxWidth: { xs: "calc(100vw - 32px)", sm: 400 },
              width: { xs: "calc(100vw - 32px)", sm: "300px" },
            },
          },
          list: {
            "aria-labelledby": "unity-courtesy-header-user-menu",
            sx: {
              "& .MuiListItemText-root": {
                marginLeft: { xs: "12px", sm: "16px" },
              },
              "& .MuiListItemText-primary": {
                fontSize: { xs: "13px", sm: "14px" },
                fontWeight: "400",
                color: "text.primary",
              },
              "& .MuiMenuItem-root": {
                minHeight: { xs: "44px", sm: "48px" },
                paddingY: { xs: 1, sm: 1.5 },
              },
              "& .MuiListItemIcon-root": {
                minWidth: { xs: "40px", sm: "44px" },
              },
            },
          },
        }}
      >
        {user && (
          <MenuItem
            onClick={profileDrawersEnabled ? () => {
              onClose();
              onOpenUserProfileDrawer?.();
            } : undefined}
            {...(profileDrawersEnabled
              ? telemetryProps({ name: "user_menu_profile_header", section: "user_menu", label: "Profile Header" })
              : {})}
            sx={{
              cursor: profileDrawersEnabled ? "pointer" : "default",
              ...(profileDrawersEnabled ? {} : {
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "&:focus": {
                  backgroundColor: "transparent",
                },
              }),
            }}
            tabIndex={profileDrawersEnabled ? 0 : -1}
            aria-label={
              profileDrawersEnabled
                ? `Open user profile: ${user.displayName} Email: ${user.email}`
                : `User: ${user.displayName} Email: ${user.email}`
            }
          >
            {userAvatar && <ListItemIcon>{userAvatar}</ListItemIcon>}
            <ListItemText
              primary={user.displayName}
              secondary={user.email}
              slotProps={{
                primary: {
                  noWrap: true,
                  variant: "body1",
                  title: user.displayName,
                },
                secondary: {
                  noWrap: true,
                  title: user.email,
                  variant: "body2",
                },
              }}
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: { xs: "15px", sm: "16px" },
                },
              }}
            />
          </MenuItem>
        )}
        {user && <Divider />}
        {messages && (
          <MenuItem
            component={RouterNavLink}
            to={AppendIfPrefixExists("/support-center/message-center")}
            onClick={onClose}
            onKeyDown={handleLinkKeyDown}
            {...telemetryProps({ name: "user_menu_message_center", section: "user_menu", label: "Message Center" })}
            aria-label="Message Center"
          >
            <ListItemIcon>
              <MarkEmailUnreadIcon />
            </ListItemIcon>
            <ListItemText primary="Message Center" />
          </MenuItem>
        )}
        <Tooltip
          title={themeMode === "light" ? DarkModeBetaWarning : ""}
          placement="left"
          arrow
          disableHoverListener={themeMode === "dark"}
          disableFocusListener={themeMode === "dark"}
          disableTouchListener={themeMode === "dark"}
        >
          <MenuItem
            onClick={handleToggleThemeMode}
            {...telemetryProps({
              name: "user_menu_toggle_theme",
              section: "user_menu",
              label: themeMode === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode",
            })}
            aria-label={`Switch to ${themeMode === "dark" ? "light" : "dark"} mode`}
          >
            <ListItemIcon>
              {themeMode === "light" ? <DarkMode /> : <LightMode />}
            </ListItemIcon>
            <ListItemText
              primary={
                themeMode === "dark"
                  ? "Switch to Light Mode"
                  : "Switch to Dark Mode (Beta)"
              }
            />
          </MenuItem>
        </Tooltip>
        {unityStatusData?.status === UnityExperienceStatus.InExperience && (
          <MenuItem
            onClick={() => setDialogOpen(true)}
            {...telemetryProps({ name: "user_menu_return_classic", section: "user_menu", label: "Return to Classic SHI.com" })}
            aria-label="Return to Classic SHI.com Experience"
          >
            <ListItemIcon>
              <ForkLeftIcon />
            </ListItemIcon>
            <ListItemText primary="Use Legacy SHI.com" />
          </MenuItem>
        )}
        <MenuItem
          component="a"
          href={`${shiComBaseUrl}/customerservice/frequently-asked-questions`}
          onClick={onClose}
          onKeyDown={handleLinkKeyDown}
          {...telemetryProps({ name: "user_menu_faqs", section: "user_menu", label: "FAQs" })}
          aria-label="FAQs"
        >
          <ListItemIcon>
            <HelpRounded />
          </ListItemIcon>
          <ListItemText primary="FAQs" />
        </MenuItem>
        <Divider />
        {account && (
          <MenuItem
            component="a"
            href={`${shiComBaseUrl}/account/account-settings`}
            onClick={onClose}
            onKeyDown={handleLinkKeyDown}
            {...telemetryProps({ name: "user_menu_account_settings", section: "user_menu", label: "Account Settings" })}
            aria-label="Account Settings"
          >
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="Account Settings" />
          </MenuItem>
        )}
        <MenuItem
          component={addressManagementEnabled ? RouterNavLink : "a"}
          {...(addressManagementEnabled
            ? { to: AppendIfPrefixExists("/my-addresses") }
            : { href: `${shiComBaseUrl}/account/address-manager` })}
          onClick={onClose}
          onKeyDown={handleLinkKeyDown}
          {...telemetryProps({ name: "user_menu_my_addresses", section: "user_menu", label: "My Addresses" })}
          aria-label="My Addresses"
        >
          <ListItemIcon>
            <LocationCityIcon />
          </ListItemIcon>
          <ListItemText primary="My Addresses" />
        </MenuItem>
        {changeOrgUnit && <Divider />}
        {changeOrgUnit && (
          <MenuItem
            component={storefrontSwitcher.enabled ? "li" : "a"}
            href={storefrontSwitcher.enabled ? undefined : `${shiComBaseUrl}/account/ChangeCatalog`}
            onClick={() => {
              onClose();
              if (storefrontSwitcher.enabled) {
                storefrontSwitcher.open("user_menu");
              }
            }}
            onKeyDown={
              storefrontSwitcher.enabled ? undefined : handleLinkKeyDown
            }
            {...telemetryProps({ name: "user_menu_switch_catalog", section: "user_menu", label: switchLabel })}
            aria-label={switchLabel}
          >
            <ListItemIcon>
              <SwapHorizOutlined />
            </ListItemIcon>
            <ListItemText
              primary={switchLabel}
              sx={{
                "& .MuiListItemText-primary": {
                  ...MENU_ITEM_CONTEXT_SWITCH_TYPOGRAPHY,
                },
              }}
            />
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          component={RouterNavLink}
          to={AppendIfPrefixExists("/logout")}
          onClick={onClose}
          onKeyDown={handleLinkKeyDown}
          {...telemetryProps({ name: "user_menu_sign_out", section: "user_menu", label: "Sign Out" })}
          aria-label="SIGN OUT"
        >
          <ListItemIcon>
            <LogoutOutlined />
          </ListItemIcon>
          <ListItemText
            primary="SIGN OUT"
            sx={{
              "& .MuiListItemText-primary": {
                ...MENU_ITEM_CONTEXT_SWITCH_TYPOGRAPHY,
              },
            }}
          />
        </MenuItem>
      </Menu>
      <UseLegacyShiComDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleConfirmRevert}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CourtesyUserMenu;
